import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import { useCentinelaDemo } from "../../../Layouts/monitoreo-ia/services/centinelaDemoMode";
import {
  buildFleetSnapshot,
  type FleetSnapshot,
} from "../../../Layouts/monitoreo-ia/services/fleetSnapshot";
import {
  useAiChatStore,
  type ChatPendingAction,
  type ChatThreadSummary,
} from "../../../store/aiChatStore";
import { buildSimulatedReply, buildWelcome } from "./simulatedChat";
import { buildPlainWelcome, type WelcomeContext, type WelcomeFleet } from "./welcome";

const REPLY_DELAY_MS = 1100;
const WELCOME_DELAY_MS = 1400;

const welcomeFleetOf = (snapshot: FleetSnapshot | null): WelcomeFleet | null =>
  snapshot && {
    total: snapshot.totals.units,
    moving: snapshot.totals.driving,
    stopped: snapshot.totals.stopped + snapshot.totals.ralenti,
  };

export interface ConversationMessage {
  id: string;
  role: "user" | "assistant";
  content: string;
  isLoading?: boolean;
}

export interface UseCentinelaConversationOptions {
  context?: WelcomeContext;
  /** La pantalla real, cuando no coincide con el tema. Por defecto, el tema. */
  surface?: WelcomeContext;
  operatorName?: string;
  autoStart?: boolean;
}

export interface UseCentinelaConversationReturn {
  messages: ConversationMessage[];
  welcome: string | null;
  isWelcoming: boolean;
  isLoading: boolean;
  isSimulated: boolean;
  /** A change the assistant proposed, waiting for the person to decide. */
  pendingAction: ChatPendingAction | null;
  isDeciding: boolean;
  decide: (decision: "approve" | "reject") => void;
  sendMessage: (text: string, overrideContext?: WelcomeContext) => void;
  clear: () => void;
  threads: ChatThreadSummary[];
  isLoadingThreads: boolean;
  isOpeningThread: boolean;
  threadsError: string | null;
  loadThreads: () => void;
  openThread: (id: string) => void;
  removeThread: (id: string) => void;
}

export function useCentinelaConversation({
  context = "general",
  surface,
  operatorName,
  autoStart = false,
}: UseCentinelaConversationOptions = {}): UseCentinelaConversationReturn {
  const { t } = useTranslation("common");

  const storeMessages = useAiChatStore((state) => state.messages);
  const storeLoading = useAiChatStore((state) => state.isLoading);
  const storeSend = useAiChatStore((state) => state.sendMessage);
  const storeClear = useAiChatStore((state) => state.clearMessages);
  const storePending = useAiChatStore((state) => state.pendingAction);
  const storeDeciding = useAiChatStore((state) => state.isDeciding);
  const storeDecide = useAiChatStore((state) => state.decideAction);
  const threads = useAiChatStore((state) => state.threads);
  const isLoadingThreads = useAiChatStore((state) => state.isLoadingThreads);
  const isOpeningThread = useAiChatStore((state) => state.isOpeningThread);
  const threadsError = useAiChatStore((state) => state.threadsError);
  const storeLoadThreads = useAiChatStore((state) => state.loadThreads);
  const storeOpenThread = useAiChatStore((state) => state.openThread);
  const storeRemoveThread = useAiChatStore((state) => state.removeThread);
  const storeRestoreThread = useAiChatStore((state) => state.restoreLastThread);

  const [demoMessages, setDemoMessages] = useState<ConversationMessage[]>([]);
  const [isReplying, setIsReplying] = useState(false);
  const [welcome, setWelcome] = useState<string | null>(null);
  const [isWelcoming, setIsWelcoming] = useState(false);
  const idRef = useRef(0);

  const isSimulated = useCentinelaDemo();

  const nextId = () => {
    idRef.current += 1;
    return `demo-msg-${idRef.current}`;
  };

  const sendSimulated = useCallback(
    (text: string) => {
      const userId = nextId();
      const placeholderId = nextId();

      setDemoMessages((prev) => [
        ...prev,
        { id: userId, role: "user", content: text },
        { id: placeholderId, role: "assistant", content: "", isLoading: true },
      ]);
      setIsReplying(true);

      setTimeout(() => {
        const reply = buildSimulatedReply(text, t);
        setDemoMessages((prev) =>
          prev.map((message) =>
            message.id === placeholderId
              ? { ...message, content: reply, isLoading: false }
              : message,
          ),
        );
        setIsReplying(false);
      }, REPLY_DELAY_MS);
    },
    [t],
  );

  const sendMessage = useCallback(
    (text: string, overrideContext?: WelcomeContext) => {
      const value = text.trim();
      if (value.length === 0) return;

      const finalContext = overrideContext ?? context;
      // If context is overridden, also change surface to match it (unless surface was explicitly configured)
      const finalSurface = overrideContext ? overrideContext : (surface ?? finalContext);

      console.log("[useCentinelaConversation] sendMessage:", {
        messagePreview: value.substring(0, 50),
        overrideContext,
        hookContext: context,
        hookSurface: surface,
        finalContext,
        finalSurface,
        isSimulated,
      });

      const payload = { context: finalContext, surface: finalSurface };
      console.log("[useCentinelaConversation] Sending to store:", {
        messagePreview: value.substring(0, 50),
        payload,
      });

      isSimulated
        ? sendSimulated(value)
        : void storeSend(value, payload);
    },
    [context, isSimulated, sendSimulated, storeSend, surface],
  );

  const startWelcome = useCallback(() => {
    setWelcome(null);
    setIsWelcoming(true);

    const timer = setTimeout(() => {
      setWelcome(
        isSimulated
          ? buildWelcome(context, t, operatorName)
          : buildPlainWelcome(context, t, operatorName, welcomeFleetOf(buildFleetSnapshot())),
      );
      setIsWelcoming(false);
    }, WELCOME_DELAY_MS);

    return () => clearTimeout(timer);
  }, [context, isSimulated, operatorName, t]);

  useEffect(() => {
    if (!autoStart) return;
    if (isSimulated) return startWelcome();

    let cancelled = false;
    let stopWelcome: (() => void) | undefined;

    void storeRestoreThread().then((restored) => {
      if (cancelled) return;
      if (!restored) stopWelcome = startWelcome();
    });

    return () => {
      cancelled = true;
      stopWelcome?.();
    };
  }, [autoStart, isSimulated, startWelcome, storeRestoreThread]);

  const openThread = useCallback(
    (id: string) => {
      void storeOpenThread(id).then((opened) => {
        opened && setWelcome(null);
      });
    },
    [storeOpenThread],
  );

  const removeThread = useCallback(
    (id: string) => {
      const wasOpen = useAiChatStore.getState().threadId === id;
      void storeRemoveThread(id).then(() => {
        wasOpen && startWelcome();
      });
    },
    [startWelcome, storeRemoveThread],
  );

  const clear = useCallback(() => {
    setDemoMessages([]);
    setIsReplying(false);
    storeClear();
    startWelcome();
  }, [startWelcome, storeClear]);

  const messages = useMemo<ConversationMessage[]>(() => {
    if (isSimulated) return demoMessages;

    return storeMessages.map((message) => ({
      id: message.id,
      role: message.role,
      content: message.content,
      isLoading: message.isLoading,
    }));
  }, [demoMessages, isSimulated, storeMessages]);

  return {
    messages,
    welcome,
    isWelcoming,
    isLoading: isSimulated ? isReplying : storeLoading,
    isSimulated,
    // Demo mode never proposes a change: its replies are canned text, so there
    // is nothing to confirm and the card must not appear.
    pendingAction: isSimulated ? null : storePending,
    isDeciding: storeDeciding,
    decide: (decision) => storeDecide(decision),
    sendMessage,
    clear,
    threads: isSimulated ? [] : threads,
    isLoadingThreads,
    isOpeningThread,
    threadsError,
    loadThreads: storeLoadThreads,
    openThread,
    removeThread,
  };
}

export default useCentinelaConversation;
